import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { type Icon } from "iconsax-react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { CloseIcon, MaximizeIcon, MinimizeIcon } from "@/shared/assets/icons";
import { useSlideAnimation, EASING } from "@/shared/hooks";
import { ScrollArea } from "../ScrollArea";
import Button from "../Button";

type IconComponent = Icon | React.ComponentType<{ size?: number | string; color?: string; className?: string }>;

interface Props {
  icon: IconComponent | null;
  children?: React.ReactNode;
  onClose: () => void;
  resize?: boolean;
  onFullScreenChange?: (isFullScreen: boolean) => void;
  hasBackground?: boolean;
  zIndex?: number;
  width?: string;
  iconClassName?: string;
  footer?: React.ReactNode | ((close: () => void) => React.ReactNode);
  headerExtra?: React.ReactNode;
  closeButtonLabel?: string;
  hidden?: boolean;
  ariaLabel?: string;
}

export default function ModalForm({
  icon,
  children,
  onClose,
  resize = false,
  onFullScreenChange,
  hasBackground = true,
  zIndex = 10000,
  width,
  iconClassName,
  footer,
  headerExtra,
  closeButtonLabel,
  hidden = false,
  ariaLabel,
}: Props) {
  const { t } = useTranslation("Common");
  const [isFullScreen, setIsFullScreen] = useState(false);
  const IconComponent = icon as IconComponent | null;
  const { phase, canAnimate, isInteractive, handleClose, reopen } = useSlideAnimation(onClose);

  const prevHiddenRef = useRef(hidden);
  useEffect(
    function resetPhaseOnReopen() {
      if (prevHiddenRef.current === true && hidden === false) {
        reopen();
      }
      prevHiddenRef.current = hidden;
    },
    [hidden, reopen],
  );

  useEffect(
    function handleEscapeKey() {
      function handleKeyDown(e: KeyboardEvent) {
        if (e.key !== "Escape") return;
        if (hidden) return;
        const hasHigherOverlay = document.querySelector(".z-\\[10200\\]");
        if (hasHigherOverlay) return;
        handleClose();
      }
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    },
    [handleClose, hidden],
  );

  function handleFullScreenToggle() {
    const newValue = !isFullScreen;
    setIsFullScreen(newValue);
    onFullScreenChange?.(newValue);
  }

  const isOpen = phase === "open";
  const isInert = hidden || !isOpen;

  return createPortal(
    <div
      className={cn(
        "fixed top-0 left-0 w-screen h-screen p-5 flex justify-end cursor-pointer",
        hasBackground && "surface-backdrop blur-level-1",
      )}
      style={{
        zIndex,
        opacity: isOpen && !hidden ? 1 : 0,
        transition: canAnimate ? `opacity 300ms ${EASING}` : "none",
        pointerEvents: isOpen && !hidden ? undefined : "none",
      }}
      aria-hidden={isInert}
      inert={isInert}
      onClick={isInteractive ? handleClose : undefined}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || t("dialog.defaultLabel")}
        className={cn(
          "h-full border surface-component-stroke dark:border-grey-100 surface-base-fill radius-2xl flex flex-col cursor-default",
          resize && isFullScreen ? "w-7xl" : width || "w-xl",
        )}
        style={{
          translate: isOpen && !hidden ? "none" : "100% 0",
          transition: canAnimate ? `translate 300ms ${EASING}, width 300ms ${EASING}` : "none",
          pointerEvents: isInteractive ? undefined : "none",
        }}
        onClick={(e) => {
          e.stopPropagation();
          const overlay = e.currentTarget.querySelector(".z-\\[10200\\]");
          if (overlay && !overlay.contains(e.target as Node)) {
            document.dispatchEvent(new MouseEvent("mousedown"));
          }
        }}>
        <div className="flex justify-between items-center px-7 pt-7">
          {icon && IconComponent && (
            <div
              className={cn(
                "w-10 aspect-square flex justify-center items-center radius-xs",
                iconClassName || "content-base-secondary surface-base-fill button-secondary-normal",
              )}>
              <IconComponent size={20} color="currentColor" />
            </div>
          )}

          <div className="flex justify-end items-center gap-2">
            {headerExtra}
            {resize && (
              <button
                className="w-10 aspect-square flex justify-center items-center cursor-pointer"
                onClick={handleFullScreenToggle}>
                <span className="content-base-secondary">{isFullScreen ? <MinimizeIcon /> : <MaximizeIcon />}</span>
              </button>
            )}
            <button
              className="w-10 aspect-square flex justify-center items-center cursor-pointer content-base-secondary"
              onClick={handleClose}>
              <CloseIcon />
            </button>
          </div>
        </div>

        <ScrollArea className="flex-1 min-h-0 mt-5">
          <div className="px-7 pb-7">{children}</div>
        </ScrollArea>
        {footer && (
          <div className={cn("shrink-0 pt-5 px-7", closeButtonLabel ? "pb-2" : "pb-7")}>
            {typeof footer === "function" ? footer(handleClose) : footer}
          </div>
        )}
        {closeButtonLabel && (
          <div className="shrink-0 px-7 pb-7">
            <Button
              variant="tertiary"
              onClick={handleClose}
              className="w-full py-2.5 radius-md border surface-base-stroke text-body-bold-md content-base-primary">
              {closeButtonLabel}
            </Button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

